/**
 * ofertaPtService.js — la landing del «Plano Google Maps 7 Dias».
 *
 * La misma landing tiene dos puertas y este servicio habla con las dos:
 *
 *   con sesión (`/plano-google-maps`)
 *     GET  /billing/oferta-pt/landing
 *   con el enlace del correo (`/oferta/<token>`, sin sesión)
 *     GET  /public/oferta-pt/<token>/          → lo mismo, más si el enlace vale
 *     POST /public/oferta-pt/<token>/entrar/   → abre la sesión
 *
 * La tarjeta del negocio de la V6, si el GET dice que falta (`fichaPendente`):
 *     POST …/ficha                              → la pide a Google y la guarda
 *
 * Y el pago, siempre con sesión:
 *     POST /billing/oferta-pt/checkout          → { url } de Stripe
 *
 * El GET del token deja además la cookie `csrftoken`, que `httpClient` manda en
 * los POST: abrir una sesión sin CSRF permitiría a otra web meter al visitante
 * en la cuenta de otro.
 */
import httpClient from './httpClient';
import { mapOfertaPt } from './gbusinessService';
import { gaIdsPayload, pushEvent } from '@/lib/ga';

const PUBLIC = '/public/oferta-pt';

const num = (v) => (Number.isFinite(v) ? v : null);

function mapDiagnostico(d) {
  if (!d || typeof d !== 'object') return null;
  return {
    estado: d.estado || 'pronto',
    fonte: d.fonte || null,
    negocio: d.negocio || '',
    palavraChave: d.palavra_chave || '',
    posicaoMedia: num(d.posicao_media),
    porDelante: num(d.por_delante),
    seoScore: num(d.seo_score),
    resenasTotal: num(d.resenas_total),
    resenasSemResposta: num(d.resenas_sem_resposta),
  };
}

/** La tarjeta del negocio (`QR/billing/oferta_pt_ficha.py`), o null. */
export function mapFicha(f) {
  if (!f || typeof f !== 'object' || !f.nome) return null;
  return {
    placeId: f.place_id || null,
    nome: f.nome,
    foto: f.foto || null,
    nota: num(f.nota),
    avaliacoes: num(f.avaliacoes),
    categoria: f.categoria || '',
    morada: f.morada || '',
  };
}

/** El payload de la landing en camelCase (`QR/billing/oferta_pt_landing.py`). */
export function mapLanding(data) {
  const escaneo = data?.escaneo || {};
  return {
    nombre: data?.nombre || '',
    negocio: data?.negocio || '',
    oferta: mapOfertaPt(data?.oferta),
    // Sus locales activos (1 si no tiene): donde empieza el selector.
    locais: data?.checkout?.locais || 1,
    gmbConectado: !!data?.gmb?.conectado,
    diagnostico: mapDiagnostico(data?.diagnostico),
    escaneo: {
      disponivel: !!escaneo.disponivel,
      restantes: num(escaneo.restantes) ?? 0,
      prefill: escaneo.prefill
        ? { placeId: escaneo.prefill.place_id, nome: escaneo.prefill.nome || '' }
        : null,
    },
    // La V6: su ficha (o `fichaPendente` si hay que pedirla con el POST), sus
    // Tapstar y, sin GMB, las búsquedas que se le proponen.
    ficha: mapFicha(data?.ficha),
    fichaPendente: data?.ficha_pendiente === true,
    sugestoes: Array.isArray(data?.sugestoes) ? data.sugestoes.filter(Boolean) : [],
    dispositivos: num(data?.dispositivos) ?? 0,
    // Solo con token: si el enlace sigue valiendo y, si no, por qué.
    puedeEntrar: data?.puede_entrar !== false,
    motivo: data?.motivo || null,
    // Enlace de una prueba de Admin → Campaña pt: la landing no mide sus CTAs.
    prueba: data?.prueba === true,
    // La prueba a una cuenta admin: se ve la landing, pero no entra ni compra.
    soloVer: data?.solo_ver === true,
    // Solo con un enlace válido de cliente: su id para identificarle en PostHog.
    posthogId: data?.posthog_id || null,
  };
}

export async function getOfertaPtLanding(token) {
  if (token) {
    const { data } = await httpClient.get(`${PUBLIC}/${encodeURIComponent(token)}/`);
    return mapLanding(data);
  }
  const { data } = await httpClient.get('/billing/oferta-pt/landing');
  return mapLanding(data);
}

export async function entrarOfertaPt(token) {
  const { data } = await httpClient.post(`${PUBLIC}/${encodeURIComponent(token)}/entrar/`);
  return { redirect: data.redirect || '/plano-google-maps' };
}

// ── El diagnóstico de quien no tiene la ficha conectada ──────────────────────
// Mismas rutas con sesión (`/billing/oferta-pt/…`) o con el token del correo
// (`/public/oferta-pt/<token>/…/`, con barra final como el resto de públicas).

function rutaDiag(token, resto) {
  return token
    ? `${PUBLIC}/${encodeURIComponent(token)}/${resto}/`
    : `/billing/oferta-pt/${resto}`;
}

export async function buscarNegocios(token, q) {
  const { data } = await httpClient.get(rutaDiag(token, 'negocios'), { params: { q } });
  return (data.resultados || []).map((r) => ({ placeId: r.place_id, nome: r.nome, morada: r.morada }));
}

export async function prepararDiagnostico(token, placeId) {
  const { data } = await httpClient.post(rutaDiag(token, 'diagnostico/preparar'), { place_id: placeId });
  return { placeId: data.place_id, nome: data.nome || '', morada: data.morada || '', palavras: data.palavras || [] };
}

function mapEstado(data) {
  return {
    diagnostico: mapDiagnostico(data?.diagnostico),
    progresso: data?.progresso
      ? { total: data.progresso.total || 0, feitos: data.progresso.feitos || 0 }
      : null,
  };
}

export async function lancarDiagnostico(token, { placeId, palavra }) {
  const { data } = await httpClient.post(rutaDiag(token, 'diagnostico'), { place_id: placeId, palavra });
  return mapEstado(data);
}

/** La tarjeta del negocio, pidiéndola a Google si no está guardada. */
export async function pedirFicha(token) {
  const { data } = await httpClient.post(rutaDiag(token, 'ficha'));
  return {
    ficha: mapFicha(data?.ficha),
    sugestoes: Array.isArray(data?.sugestoes) ? data.sugestoes.filter(Boolean) : [],
  };
}

export async function getDiagnostico(token) {
  const { data } = await httpClient.get(rutaDiag(token, 'diagnostico'));
  return mapEstado(data);
}

/**
 * El «Começar o meu plano». Lo que se cobra lo decide el servidor (anual, sin
 * trial, un asiento por local); aquí solo viaja el botón que se pulsó y desde
 * qué pantalla, para atribuirle el alta (§124, §125).
 */
export async function criarCheckoutOferta({ ctaId, locais }) {
  const body = { cta_id: ctaId, lang: 'pt' };
  // El selector de locales (1–30); el servidor lo valida y, sin él, pone uno
  // por local activo.
  if (Number.isInteger(locais)) body.locais = locais;
  // Sin el token: `/oferta/<token>` abre la sesión del cliente y no puede viajar
  // a la metadata de Stripe (el servidor lo recorta también, `clean_path`).
  if (typeof window !== 'undefined') {
    const path = window.location?.pathname || '';
    body.cta_path = path.toLowerCase().startsWith('/oferta/') ? '/oferta' : path;
  }
  Object.assign(body, gaIdsPayload());
  pushEvent('begin_checkout', { period: 'annual', oferta: 'pt' });
  const { data } = await httpClient.post('/billing/oferta-pt/checkout', body);
  return data.url;
}
